import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ChevronRight, ChevronLeft, FileText, Shield, Clock, CheckCircle, Lock, Download, ArrowRight, HelpCircle, Eye } from "lucide-react";
import axios from "axios";
import { CFELogo } from "@/components/CFELogo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STRIPE_CHECKOUT_URL = "https://buy.stripe.com/bJeeVc8002hvaz7cvOgjC05";

// Composant FormField STABLE - défini à l'extérieur pour éviter le remount
const FormField = ({ label, name, value, onChange, type = "text", placeholder, help, required = false, ...props }) => (
  <div className="form-field space-y-1">
    <Label htmlFor={name} className="text-[#0F172A] font-medium text-sm">
      {label} {required && <span className="text-red-500">*</span>}
    </Label>
    <Input
      id={name}
      name={name}
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className="h-11 border-slate-200 text-base"
      data-testid={`input-${name}`}
      {...props}
    />
    {help && <p className="text-xs text-[#94A3B8]">{help}</p>}
  </div>
);

// Étapes conformes au formulaire 1447-C-SD
const STEPS = [
  { id: 1, title: "A1 - Identité", section: "A1" },
  { id: 2, title: "A2/A3 - Activité", section: "A2-A3" },
  { id: 3, title: "B - Entreprise", section: "B" },
  { id: 4, title: "C - Locaux", section: "C" },
  { id: 5, title: "D - Exonérations", section: "D" },
];

const FAQ_ITEMS = [
  {
    question: "Qu'est-ce que la CFE ?",
    answer: "La Cotisation Foncière des Entreprises (CFE) est un impôt local dû par toutes les entreprises et les travailleurs indépendants, y compris les auto-entrepreneurs. Elle est basée sur la valeur locative des biens utilisés pour l'activité professionnelle."
  },
  {
    question: "Qui doit remplir le formulaire 1447-C-SD ?",
    answer: "Le formulaire 1447-C-SD doit être rempli par tout nouvel auto-entrepreneur lors de sa première année d'activité. Il permet de déclarer votre établissement aux services fiscaux pour le calcul de votre CFE."
  },
  {
    question: "Quand dois-je envoyer ma déclaration ?",
    answer: "La déclaration initiale doit être envoyée avant le 31 décembre de l'année de création de votre activité. Pour les années suivantes, seul un changement de situation nécessite une nouvelle déclaration."
  },
  {
    question: "Comment envoyer ma déclaration ?",
    answer: "Une fois votre PDF généré, vous pouvez l'imprimer et l'envoyer par courrier au Service des Impôts des Entreprises (SIE) dont dépend votre activité, ou le déposer sur votre espace professionnel impots.gouv.fr."
  },
  {
    question: "Les auto-entrepreneurs sont-ils exonérés de CFE ?",
    answer: "Les auto-entrepreneurs bénéficient d'une exonération de CFE la première année d'activité. Des exonérations peuvent également s'appliquer selon votre chiffre d'affaires ou votre zone d'implantation."
  },
];

const currentYear = new Date().getFullYear();

export default function HomePage() {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [declarationId, setDeclarationId] = useState(null);
  const formRef = useRef(null);

  const [formData, setFormData] = useState({
    // ========== A1 - IDENTIFICATION ==========
    denomination: "",
    siret: "",
    code_nace: "",
    adresse_ligne1: "",
    adresse_ligne2: "",
    code_postal: "",
    ville: "",
    adresse_envoi_ligne1: "",
    adresse_envoi_ligne2: "",
    code_postal_envoi: "",
    ville_envoi: "",
    use_same_address: true,
    activite_principale: "",
    activite_secondaire: "",
    inscription_rm: false,
    
    // ========== A2 - ACTIVITÉ DOMICILE ==========
    activite_domicile: false,
    surface_domicile: "",
    
    // ========== A3 - ORIGINE ==========
    origine_creation: true,
    origine_debut_activite: false,
    origine_acquisition: false,
    origine_transfert: false,
    origine_apport: false,
    origine_scission: false,
    origine_fusion: false,
    
    // ========== A4 - ANCIEN EXPLOITANT ==========
    ancien_exploitant_nom: "",
    ancien_exploitant_activite: "",
    
    // ========== B1 - RENSEIGNEMENTS ENTREPRISE ==========
    date_creation_entreprise: "",
    effectif_salaries: "",
    effectif_apprentis: "",
    effectif_handicapes: "",
    effectif_artisanal: "",
    chiffre_affaires_ht: "",
    recettes_location_nue: "",
    
    // ========== B2 - RENSEIGNEMENTS ÉTABLISSEMENT ==========
    etablissement_principal: true,
    effectif_etablissement: "",
    activite_saisonniere: false,
    duree_exploitation_semaines: "",
    micro_entrepreneur: true,
    date_entree_micro: "",
    activite_temps_partiel: false,
    
    // ========== C - BIENS ==========
    bien_nature: "",
    bien_adresse: "",
    bien_situation: "",
    bien_lot_copro: "",
    bien_occupant_precedent: "",
    bien_date_occupation: "",
    bien_superficie_totale: "",
    superficie_professionnel: "",
    superficie_industriel: "",
    superficie_commercial: "",
    superficie_habitation: "",
    surface_magasin: "",
    surface_entrepot: "",
    surface_bureaux: "",
    surface_autres: "",
    statut_proprietaire: false,
    statut_locataire: false,
    statut_sous_locataire: false,
    statut_domiciliation_commerciale: false,
    statut_domiciliation_gratuite: false,
    proprietaire_nom_adresse: "",
    
    // ========== D - EXONÉRATIONS ==========
    exoneration_entreprise_nouvelle: true,
    exoneration_zfu: false,
    exoneration_autre: false,
    exoneration_precision: "",
    
    // ========== MÉTADONNÉES ==========
    annee_declaration: String(currentYear),
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleCheckboxChange = (name, checked) => {
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  const validateStep = (step) => {
    switch (step) {
      case 1: // A1 - Identification
        if (!formData.denomination.trim()) {
          toast.error("Veuillez renseigner la dénomination ou votre nom et prénom");
          return false;
        }
        if (!formData.siret.trim() || formData.siret.replace(/\s/g, '').length !== 14) {
          toast.error("Le numéro SIRET doit contenir 14 chiffres");
          return false;
        }
        if (!formData.code_nace.trim()) {
          toast.error("Veuillez renseigner le code NACE/APE");
          return false;
        }
        if (!formData.adresse_ligne1.trim() || !formData.code_postal.trim() || !formData.ville.trim()) {
          toast.error("Veuillez remplir l'adresse complète de l'établissement");
          return false;
        }
        if (!formData.activite_principale.trim()) {
          toast.error("Veuillez renseigner l'activité principale");
          return false;
        }
        return true;
      case 2: // A2/A3 - Activité & Origine
        // Au moins une origine doit être cochée
        const origines = [
          formData.origine_creation, formData.origine_debut_activite,
          formData.origine_acquisition, formData.origine_transfert,
          formData.origine_apport, formData.origine_scission, formData.origine_fusion
        ];
        if (!origines.some(o => o)) {
          toast.error("Veuillez sélectionner l'origine de l'établissement");
          return false;
        }
        return true;
      case 3: // B - Entreprise
        if (!formData.date_creation_entreprise) {
          toast.error("Veuillez renseigner la date de création de l'entreprise");
          return false;
        }
        return true;
      case 4: // C - Locaux
        // Si pas domicile, les infos locaux sont nécessaires
        if (!formData.activite_domicile && !formData.bien_nature.trim()) {
          toast.error("Veuillez renseigner la nature du bien ou cocher 'Activité à domicile'");
          return false;
        }
        return true;
      case 5: // D - Exonérations
        return true;
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep) && currentStep < STEPS.length) {
      setCurrentStep(prev => prev + 1);
      window.scrollTo({ top: formRef.current?.offsetTop - 100, behavior: 'smooth' });
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
      window.scrollTo({ top: formRef.current?.offsetTop - 100, behavior: 'smooth' });
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;
    
    setIsSubmitting(true);
    try {
      // Préparer les données - convertir les chaînes vides en null pour les champs optionnels numériques
      const submitData = {
        ...formData,
        surface_domicile: formData.surface_domicile ? parseFloat(formData.surface_domicile) : null,
        effectif_salaries: formData.effectif_salaries ? parseInt(formData.effectif_salaries) : null,
        effectif_apprentis: formData.effectif_apprentis ? parseInt(formData.effectif_apprentis) : null,
        effectif_handicapes: formData.effectif_handicapes ? parseInt(formData.effectif_handicapes) : null,
        effectif_artisanal: formData.effectif_artisanal ? parseInt(formData.effectif_artisanal) : null,
        chiffre_affaires_ht: formData.chiffre_affaires_ht ? parseFloat(formData.chiffre_affaires_ht) : null,
        recettes_location_nue: formData.recettes_location_nue ? parseFloat(formData.recettes_location_nue) : null,
        effectif_etablissement: formData.effectif_etablissement ? parseInt(formData.effectif_etablissement) : null,
        duree_exploitation_semaines: formData.duree_exploitation_semaines ? parseInt(formData.duree_exploitation_semaines) : null,
        bien_superficie_totale: formData.bien_superficie_totale ? parseFloat(formData.bien_superficie_totale) : null,
        superficie_professionnel: formData.superficie_professionnel ? parseFloat(formData.superficie_professionnel) : null,
        superficie_industriel: formData.superficie_industriel ? parseFloat(formData.superficie_industriel) : null,
        superficie_commercial: formData.superficie_commercial ? parseFloat(formData.superficie_commercial) : null,
        superficie_habitation: formData.superficie_habitation ? parseFloat(formData.superficie_habitation) : null,
        surface_magasin: formData.surface_magasin ? parseFloat(formData.surface_magasin) : null,
        surface_entrepot: formData.surface_entrepot ? parseFloat(formData.surface_entrepot) : null,
        surface_bureaux: formData.surface_bureaux ? parseFloat(formData.surface_bureaux) : null,
        // Convertir chaînes vides en null pour les champs texte optionnels
        adresse_ligne2: formData.adresse_ligne2 || null,
        adresse_envoi_ligne1: formData.use_same_address ? null : (formData.adresse_envoi_ligne1 || null),
        adresse_envoi_ligne2: formData.use_same_address ? null : (formData.adresse_envoi_ligne2 || null),
        code_postal_envoi: formData.use_same_address ? null : (formData.code_postal_envoi || null),
        ville_envoi: formData.use_same_address ? null : (formData.ville_envoi || null),
        activite_secondaire: formData.activite_secondaire || null,
        ancien_exploitant_nom: formData.ancien_exploitant_nom || null,
        ancien_exploitant_activite: formData.ancien_exploitant_activite || null,
        date_entree_micro: formData.date_entree_micro || null,
        bien_nature: formData.bien_nature || null,
        bien_adresse: formData.bien_adresse || null,
        bien_situation: formData.bien_situation || null,
        bien_lot_copro: formData.bien_lot_copro || null,
        bien_occupant_precedent: formData.bien_occupant_precedent || null,
        bien_date_occupation: formData.bien_date_occupation || null,
        surface_autres: formData.surface_autres || null,
        proprietaire_nom_adresse: formData.proprietaire_nom_adresse || null,
        exoneration_precision: formData.exoneration_precision || null,
      };

      const response = await axios.post(`${API}/declarations`, submitData);
      setDeclarationId(response.data.id);
      setShowPreview(true);
      toast.success("Votre déclaration a été générée avec succès !");
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePayment = () => {
    localStorage.setItem('cfe_declaration_id', declarationId);
    window.location.href = STRIPE_CHECKOUT_URL;
  };

  const handlePreviewPDF = async () => {
    if (!validateStep(currentStep)) return;
    
    setIsSubmitting(true);
    try {
      // Préparer les données
      const submitData = {
        ...formData,
        surface_domicile: formData.surface_domicile ? parseFloat(formData.surface_domicile) : null,
        effectif_salaries: formData.effectif_salaries ? parseInt(formData.effectif_salaries) : null,
        effectif_apprentis: formData.effectif_apprentis ? parseInt(formData.effectif_apprentis) : null,
        effectif_handicapes: formData.effectif_handicapes ? parseInt(formData.effectif_handicapes) : null,
        effectif_artisanal: formData.effectif_artisanal ? parseInt(formData.effectif_artisanal) : null,
        chiffre_affaires_ht: formData.chiffre_affaires_ht ? parseFloat(formData.chiffre_affaires_ht) : null,
        recettes_location_nue: formData.recettes_location_nue ? parseFloat(formData.recettes_location_nue) : null,
        effectif_etablissement: formData.effectif_etablissement ? parseInt(formData.effectif_etablissement) : null,
        duree_exploitation_semaines: formData.duree_exploitation_semaines ? parseInt(formData.duree_exploitation_semaines) : null,
        bien_superficie_totale: formData.bien_superficie_totale ? parseFloat(formData.bien_superficie_totale) : null,
        superficie_professionnel: formData.superficie_professionnel ? parseFloat(formData.superficie_professionnel) : null,
        superficie_industriel: formData.superficie_industriel ? parseFloat(formData.superficie_industriel) : null,
        superficie_commercial: formData.superficie_commercial ? parseFloat(formData.superficie_commercial) : null,
        superficie_habitation: formData.superficie_habitation ? parseFloat(formData.superficie_habitation) : null,
        surface_magasin: formData.surface_magasin ? parseFloat(formData.surface_magasin) : null,
        surface_entrepot: formData.surface_entrepot ? parseFloat(formData.surface_entrepot) : null,
        surface_bureaux: formData.surface_bureaux ? parseFloat(formData.surface_bureaux) : null,
        adresse_ligne2: formData.adresse_ligne2 || null,
        adresse_envoi_ligne1: formData.use_same_address ? null : (formData.adresse_envoi_ligne1 || null),
        adresse_envoi_ligne2: formData.use_same_address ? null : (formData.adresse_envoi_ligne2 || null),
        code_postal_envoi: formData.use_same_address ? null : (formData.code_postal_envoi || null),
        ville_envoi: formData.use_same_address ? null : (formData.ville_envoi || null),
        activite_secondaire: formData.activite_secondaire || null,
        ancien_exploitant_nom: formData.ancien_exploitant_nom || null,
        ancien_exploitant_activite: formData.ancien_exploitant_activite || null,
        date_entree_micro: formData.date_entree_micro || null,
        bien_nature: formData.bien_nature || null,
        bien_adresse: formData.bien_adresse || null,
        bien_situation: formData.bien_situation || null,
        bien_lot_copro: formData.bien_lot_copro || null,
        bien_occupant_precedent: formData.bien_occupant_precedent || null,
        bien_date_occupation: formData.bien_date_occupation || null,
        surface_autres: formData.surface_autres || null,
        proprietaire_nom_adresse: formData.proprietaire_nom_adresse || null,
        exoneration_precision: formData.exoneration_precision || null,
      };

      // Créer la déclaration
      const response = await axios.post(`${API}/declarations`, submitData);
      const newDeclarationId = response.data.id;
      setDeclarationId(newDeclarationId);
      
      // Ouvrir l'aperçu PDF dans un nouvel onglet
      const previewUrl = `${API}/declarations/${newDeclarationId}/preview`;
      window.open(previewUrl, "_blank", "noopener,noreferrer");
      
      toast.success("Aperçu généré ! Le PDF s'ouvre dans un nouvel onglet.");
    } catch (error) {
      console.error("Error generating preview:", error);
      toast.error("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const progressValue = (currentStep / STEPS.length) * 100;

  return (
    <div className="min-h-screen">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200 safe-area-top">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 sm:h-16">
            <a href="/" className="header-logo-group">
              <CFELogo size={28} />
              <div className="logo-text">
                <span className="font-bold text-lg text-[#0F172A]">CFE Auto-Entrepreneur</span>
                <span className="header-subtitle">Formulaire 1447-C-SD</span>
              </div>
            </a>
            <Button 
              onClick={scrollToForm}
              className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white btn-primary"
              data-testid="nav-cta-btn"
            >
              Commencer
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-section pt-32 sm:pt-32 pb-12 sm:pb-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-stretch">
            <div className="lg:col-span-7 animate-slide-up hero-text-column">
              <span className="inline-block px-3 py-1 text-sm font-medium text-[#2563EB] bg-blue-50 rounded-full mb-4">
                Formulaire 1447-C-SD
              </span>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0F172A] leading-tight mb-2">
                Formulaire CFE<br />
                <span className="text-[#2563EB]">Auto-entrepreneur</span>
              </h1>
              <p className="text-base sm:text-lg text-[#64748B] mb-6">
                Formulaire officiel 1447-C-SD pour la création d'auto-entreprise
              </p>
              <p className="text-base text-[#475569] mb-8 max-w-xl">
                Ce service permet de remplir en ligne le formulaire CFE auto-entrepreneur officiel (1447-C-SD). 
                Il génère une déclaration conforme, prête à être envoyée, sans erreur de structure.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg" 
                  onClick={scrollToForm}
                  className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white h-14 px-8 text-lg btn-primary"
                  data-testid="hero-cta-btn"
                >
                  Remplir ma déclaration CFE
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button 
                  variant="outline" 
                  size="lg"
                  onClick={() => document.getElementById('faq').scrollIntoView({ behavior: 'smooth' })}
                  className="h-14 px-8 text-lg border-slate-300 text-[#475569] hover:bg-slate-50"
                  data-testid="faq-link-btn"
                >
                  <HelpCircle className="mr-2 h-5 w-5" />
                  En savoir plus
                </Button>
              </div>
            </div>
            <div className="lg:col-span-5 animate-slide-up stagger-2 hero-image-container">
              <div className="relative h-full flex flex-col justify-center">
                <img 
                  src="https://images.unsplash.com/photo-1655988940601-7702d8685f95?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBtaW5pbWFsaXN0JTIwb2ZmaWNlJTIwZGVzayUyMHBhcGVyd29ya3xlbnwwfHx8d2hpdGV8MTc2NjIyMTc5Nnww&ixlib=rb-4.1.0&q=85"
                  alt="Espace de travail organisé"
                  className="rounded-2xl shadow-2xl w-full lg:h-[380px] object-cover"
                />
                <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-lg p-4 animate-slide-in stagger-3">
                  <div className="flex items-center gap-3">
                    <div className="bg-green-100 rounded-full p-2">
                      <CheckCircle className="h-6 w-6 text-green-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-[#0F172A] text-sm">Déjà utilisé par</p>
                      <p className="text-sm text-[#475569]">des auto-entrepreneurs</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#F1F5F9]">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <Card className="trust-badge bg-white border-slate-200 hover-lift" data-testid="trust-badge-conforme">
              <CardContent className="p-6 text-center">
                <div className="bg-blue-50 rounded-full w-14 h-14 flex items-center justify-center mx-auto mb-4">
                  <FileText className="h-7 w-7 text-[#2563EB]" />
                </div>
                <h3 className="font-semibold text-[#0F172A] mb-1">Formulaire officiel</h3>
                <p className="text-sm text-[#475569]">1447-C-SD en vigueur</p>
              </CardContent>
            </Card>
            <Card className="trust-badge bg-white border-slate-200 hover-lift" data-testid="trust-badge-secure">
              <CardContent className="p-6 text-center">
                <div className="bg-green-50 rounded-full w-14 h-14 flex items-center justify-center mx-auto mb-4">
                  <Shield className="h-7 w-7 text-green-600" />
                </div>
                <h3 className="font-semibold text-[#0F172A] mb-1">Paiement Sécurisé</h3>
                <p className="text-sm text-[#475569]">Via Stripe</p>
              </CardContent>
            </Card>
            <Card className="trust-badge bg-white border-slate-200 hover-lift" data-testid="trust-badge-rapide">
              <CardContent className="p-6 text-center">
                <div className="bg-amber-50 rounded-full w-14 h-14 flex items-center justify-center mx-auto mb-4">
                  <Clock className="h-7 w-7 text-amber-600" />
                </div>
                <h3 className="font-semibold text-[#0F172A] mb-1">5 minutes</h3>
                <p className="text-sm text-[#475569]">Temps moyen</p>
              </CardContent>
            </Card>
            <Card className="trust-badge bg-white border-slate-200 hover-lift" data-testid="trust-badge-apercu">
              <CardContent className="p-6 text-center">
                <div className="bg-purple-50 rounded-full w-14 h-14 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-7 w-7 text-purple-600" />
                </div>
                <h3 className="font-semibold text-[#0F172A] mb-1">Aperçu gratuit</h3>
                <p className="text-sm text-[#475569]">Avant paiement</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section ref={formRef} id="formulaire" className="py-20 px-4 sm:px-6 lg:px-8 form-bg-pattern scroll-mt-20">
        <div className="max-w-2xl lg:max-w-none wizard-container mx-auto">
          {!showPreview ? (
            <Card className="bg-white border-slate-200 shadow-xl" data-testid="form-card">
              <CardHeader className="pb-4">
                <CardTitle className="text-2xl text-[#0F172A]">Déclaration CFE - Formulaire 1447-C-SD</CardTitle>
                <p className="text-sm text-[#475569]">Tous les champs marqués * sont obligatoires</p>
                <div className="mt-4">
                  <div className="flex justify-between mb-2">
                    {STEPS.map((step) => (
                      <div 
                        key={step.id}
                        className={`flex flex-col items-center ${currentStep >= step.id ? 'text-[#2563EB]' : 'text-[#94A3B8]'}`}
                      >
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium mb-1 transition-colors ${
                          currentStep >= step.id ? 'bg-[#2563EB] text-white' : 'bg-slate-100 text-[#94A3B8]'
                        }`}>
                          {currentStep > step.id ? <CheckCircle className="h-4 w-4" /> : step.id}
                        </div>
                        <span className="text-xs hidden sm:block">{step.section}</span>
                      </div>
                    ))}
                  </div>
                  <Progress value={progressValue} className="h-1" />
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                
                {/* ========== ÉTAPE 1 : A1 - IDENTIFICATION ========== */}
                {currentStep === 1 && (
                  <div className="wizard-step space-y-5">
                    <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 mb-4">
                      <p className="text-sm text-[#2563EB] font-medium">A1 - Identification de l'entreprise</p>
                    </div>
                    
                    <FormField
                      label="Dénomination ou Nom et Prénom"
                      name="denomination"
                      value={formData.denomination}
                      onChange={handleInputChange}
                      placeholder="Ex: Jean Dupont ou DUPONT CONSULTING"
                      help="Nom tel qu'il apparaît sur votre extrait Kbis ou avis SIRENE"
                      required
                    />
                    
                    <div className="grid grid-cols-2 gap-4">
                      <FormField
                        label="N° SIRET"
                        name="siret"
                        value={formData.siret}
                        onChange={handleInputChange}
                        placeholder="123 456 789 00012"
                        help="14 chiffres"
                        required
                      />
                      <FormField
                        label="Code NACE/APE"
                        name="code_nace"
                        value={formData.code_nace}
                        onChange={handleInputChange}
                        placeholder="6201Z"
                        help="4 chiffres + 1 lettre"
                        required
                      />
                    </div>
                    
                    <div className="border-t border-slate-100 pt-4 mt-4">
                      <p className="text-sm font-medium text-[#0F172A] mb-3">Adresse de l'établissement</p>
                      
                      <FormField
                        label="Adresse - ligne 1"
                        name="adresse_ligne1"
                        value={formData.adresse_ligne1}
                        onChange={handleInputChange}
                        placeholder="12 rue de la Paix"
                        help="Numéro et nom de la voie"
                        required
                      />
                      
                      <div className="mt-4">
                        <FormField
                          label="Adresse - ligne 2"
                          name="adresse_ligne2"
                          value={formData.adresse_ligne2}
                          onChange={handleInputChange}
                          placeholder="Bâtiment A, Étage 2, Appartement 15"
                          help="Complément d'adresse (facultatif) : bâtiment, étage, lieu-dit..."
                        />
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 mt-4">
                        <FormField
                          label="Code postal"
                          name="code_postal"
                          value={formData.code_postal}
                          onChange={handleInputChange}
                          placeholder="75001"
                          required
                        />
                        <FormField
                          label="Ville"
                          name="ville"
                          value={formData.ville}
                          onChange={handleInputChange}
                          placeholder="Paris"
                          required
                        />
                      </div>
                    </div>
                    
                    <div className="border-t border-slate-100 pt-4 mt-4">
                      <div className="flex items-center space-x-2 mb-4">
                        <Checkbox
                          id="use_same_address"
                          checked={formData.use_same_address}
                          onCheckedChange={(checked) => handleCheckboxChange('use_same_address', checked)}
                          data-testid="checkbox-same-address"
                        />
                        <Label htmlFor="use_same_address" className="text-sm text-[#475569] cursor-pointer">
                          Adresse d'envoi de l'avis d'imposition identique
                        </Label>
                      </div>
                      
                      {!formData.use_same_address && (
                        <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
                          <p className="text-sm font-medium text-[#0F172A]">Adresse d'envoi de l'avis</p>
                          <FormField
                            label="Adresse - ligne 1"
                            name="adresse_envoi_ligne1"
                            value={formData.adresse_envoi_ligne1}
                            onChange={handleInputChange}
                            placeholder="5 avenue des Champs-Élysées"
                          />
                          <FormField
                            label="Adresse - ligne 2"
                            name="adresse_envoi_ligne2"
                            value={formData.adresse_envoi_ligne2}
                            onChange={handleInputChange}
                            placeholder="Complément (facultatif)"
                          />
                          <div className="grid grid-cols-2 gap-4">
                            <FormField
                              label="Code postal"
                              name="code_postal_envoi"
                              value={formData.code_postal_envoi}
                              onChange={handleInputChange}
                              placeholder="75008"
                            />
                            <FormField
                              label="Ville"
                              name="ville_envoi"
                              value={formData.ville_envoi}
                              onChange={handleInputChange}
                              placeholder="Paris"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div className="border-t border-slate-100 pt-4 mt-4">
                      <p className="text-sm font-medium text-[#0F172A] mb-3">Activités exercées</p>
                      
                      <FormField
                        label="Activité principale"
                        name="activite_principale"
                        value={formData.activite_principale}
                        onChange={handleInputChange}
                        placeholder="Ex: Développement informatique, Conseil en gestion..."
                        help="Description de votre activité principale"
                        required
                      />
                      
                      <div className="mt-4">
                        <FormField
                          label="Activité secondaire"
                          name="activite_secondaire"
                          value={formData.activite_secondaire}
                          onChange={handleInputChange}
                          placeholder="Ex: Formation professionnelle..."
                          help="Facultatif - si vous avez une activité complémentaire"
                        />
                      </div>
                      
                      <div className="flex items-center space-x-2 mt-4">
                        <Checkbox
                          id="inscription_rm"
                          checked={formData.inscription_rm}
                          onCheckedChange={(checked) => handleCheckboxChange('inscription_rm', checked)}
                        />
                        <Label htmlFor="inscription_rm" className="text-sm text-[#475569] cursor-pointer">
                          Inscrit au Registre des Métiers (artisan)
                        </Label>
                      </div>
                    </div>
                  </div>
                )}

                {/* ========== ÉTAPE 2 : A2/A3 - ACTIVITÉ & ORIGINE ========== */}
                {currentStep === 2 && (
                  <div className="wizard-step space-y-5">
                    <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 mb-4">
                      <p className="text-sm text-[#2563EB] font-medium">A2 - Activité à domicile / A3 - Origine</p>
                    </div>
                    
                    {/* A2 - Activité domicile */}
                    <div className="p-4 border border-slate-200 rounded-lg">
                      <p className="font-medium text-[#0F172A] mb-3">A2 - Activité exercée à domicile ou en clientèle</p>
                      <div className="flex items-start space-x-2">
                        <Checkbox
                          id="activite_domicile"
                          checked={formData.activite_domicile}
                          onCheckedChange={(checked) => handleCheckboxChange('activite_domicile', checked)}
                        />
                        <div>
                          <Label htmlFor="activite_domicile" className="text-sm text-[#475569] cursor-pointer">
                            J'exerce mon activité à mon domicile ou chez mes clients (sans autre local professionnel)
                          </Label>
                        </div>
                      </div>
                      
                      {formData.activite_domicile && (
                        <div className="mt-4 pl-6">
                          <FormField
                            label="Surface occupée pour l'activité (m²)"
                            name="surface_domicile"
                            type="number"
                            value={formData.surface_domicile}
                            onChange={handleInputChange}
                            placeholder="Ex: 15"
                            help="Surface dédiée exclusivement à votre activité professionnelle"
                          />
                        </div>
                      )}
                    </div>
                    
                    {/* A3 - Origine */}
                    <div className="p-4 border border-slate-200 rounded-lg">
                      <p className="font-medium text-[#0F172A] mb-3">A3 - Origine de l'établissement *</p>
                      <p className="text-xs text-[#94A3B8] mb-3">Cochez la ou les cases correspondant à votre situation</p>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {[
                          { name: "origine_creation", label: "Création d'établissement" },
                          { name: "origine_debut_activite", label: "Début d'activité" },
                          { name: "origine_acquisition", label: "Acquisition d'établissement" },
                          { name: "origine_transfert", label: "Transfert d'activité" },
                          { name: "origine_apport", label: "Apport" },
                          { name: "origine_scission", label: "Scission" },
                          { name: "origine_fusion", label: "Fusion" },
                        ].map((item) => (
                          <div key={item.name} className="flex items-center space-x-2">
                            <Checkbox
                              id={item.name}
                              checked={formData[item.name]}
                              onCheckedChange={(checked) => handleCheckboxChange(item.name, checked)}
                            />
                            <Label htmlFor={item.name} className="text-sm text-[#475569] cursor-pointer">
                              {item.label}
                            </Label>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* A4 - Ancien exploitant (si acquisition/transfert) */}
                    {(formData.origine_acquisition || formData.origine_transfert || formData.origine_fusion) && (
                      <div className="p-4 border border-slate-200 rounded-lg bg-slate-50">
                        <p className="font-medium text-[#0F172A] mb-3">A4 - Identification de l'ancien exploitant</p>
                        
                        <FormField
                          label="Nom de l'ancien exploitant"
                          name="ancien_exploitant_nom"
                          value={formData.ancien_exploitant_nom}
                          onChange={handleInputChange}
                          placeholder="Nom ou dénomination"
                        />
                        
                        <div className="mt-4">
                          <FormField
                            label="Activité exercée"
                            name="ancien_exploitant_activite"
                            value={formData.ancien_exploitant_activite}
                            onChange={handleInputChange}
                            placeholder="Activité de l'ancien exploitant"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* ========== ÉTAPE 3 : B - RENSEIGNEMENTS ENTREPRISE ========== */}
                {currentStep === 3 && (
                  <div className="wizard-step space-y-5">
                    <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 mb-4">
                      <p className="text-sm text-[#2563EB] font-medium">B1/B2 - Renseignements entreprise et établissement</p>
                    </div>
                    
                    {/* B1 - Entreprise */}
                    <div className="p-4 border border-slate-200 rounded-lg">
                      <p className="font-medium text-[#0F172A] mb-3">B1 - Renseignements pour l'ensemble de l'entreprise</p>
                      
                      <FormField
                        label="Date de création de l'entreprise"
                        name="date_creation_entreprise"
                        type="date"
                        value={formData.date_creation_entreprise}
                        onChange={handleInputChange}
                        required
                      />
                      
                      <div className="grid grid-cols-2 gap-4 mt-4">
                        <FormField
                          label="Effectif salariés"
                          name="effectif_salaries"
                          type="number"
                          value={formData.effectif_salaries}
                          onChange={handleInputChange}
                          placeholder="0"
                          help="Total salariés"
                        />
                        <FormField
                          label="dont apprentis"
                          name="effectif_apprentis"
                          type="number"
                          value={formData.effectif_apprentis}
                          onChange={handleInputChange}
                          placeholder="0"
                        />
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 mt-4">
                        <FormField
                          label="dont handicapés"
                          name="effectif_handicapes"
                          type="number"
                          value={formData.effectif_handicapes}
                          onChange={handleInputChange}
                          placeholder="0"
                        />
                        <FormField
                          label="dont artisanal"
                          name="effectif_artisanal"
                          type="number"
                          value={formData.effectif_artisanal}
                          onChange={handleInputChange}
                          placeholder="0"
                        />
                      </div>
                      
                      <div className="mt-4">
                        <FormField
                          label="Chiffre d'affaires HT estimé (€)"
                          name="chiffre_affaires_ht"
                          type="number"
                          value={formData.chiffre_affaires_ht}
                          onChange={handleInputChange}
                          placeholder="Ex: 35000"
                          help="Estimation pour l'année en cours"
                        />
                      </div>
                      
                      <div className="mt-4">
                        <FormField
                          label="Recettes location nue HT (€)"
                          name="recettes_location_nue"
                          type="number"
                          value={formData.recettes_location_nue}
                          onChange={handleInputChange}
                          placeholder="0"
                          help="Si vous louez des biens nus (non meublés)"
                        />
                      </div>
                    </div>
                    
                    {/* B2 - Établissement */}
                    <div className="p-4 border border-slate-200 rounded-lg">
                      <p className="font-medium text-[#0F172A] mb-3">B2 - Renseignements pour l'établissement</p>
                      
                      <div className="flex items-center space-x-2 mb-4">
                        <Checkbox
                          id="etablissement_principal"
                          checked={formData.etablissement_principal}
                          onCheckedChange={(checked) => handleCheckboxChange('etablissement_principal', checked)}
                        />
                        <Label htmlFor="etablissement_principal" className="text-sm text-[#475569] cursor-pointer">
                          Cet établissement est mon établissement principal
                        </Label>
                      </div>
                      
                      <FormField
                        label="Effectif de l'établissement"
                        name="effectif_etablissement"
                        type="number"
                        value={formData.effectif_etablissement}
                        onChange={handleInputChange}
                        placeholder="0"
                      />
                      
                      <div className="flex items-center space-x-2 mt-4">
                        <Checkbox
                          id="activite_saisonniere"
                          checked={formData.activite_saisonniere}
                          onCheckedChange={(checked) => handleCheckboxChange('activite_saisonniere', checked)}
                        />
                        <Label htmlFor="activite_saisonniere" className="text-sm text-[#475569] cursor-pointer">
                          Activité saisonnière
                        </Label>
                      </div>
                      
                      {formData.activite_saisonniere && (
                        <div className="mt-3 pl-6">
                          <FormField
                            label="Durée d'exploitation (semaines)"
                            name="duree_exploitation_semaines"
                            type="number"
                            value={formData.duree_exploitation_semaines}
                            onChange={handleInputChange}
                            placeholder="Ex: 26"
                          />
                        </div>
                      )}
                      
                      <div className="flex items-center space-x-2 mt-4">
                        <Checkbox
                          id="micro_entrepreneur"
                          checked={formData.micro_entrepreneur}
                          onCheckedChange={(checked) => handleCheckboxChange('micro_entrepreneur', checked)}
                        />
                        <Label htmlFor="micro_entrepreneur" className="text-sm text-[#475569] cursor-pointer">
                          Micro-entrepreneur (régime micro-social)
                        </Label>
                      </div>
                      
                      {formData.micro_entrepreneur && (
                        <div className="mt-3 pl-6">
                          <FormField
                            label="Date d'entrée dans le statut"
                            name="date_entree_micro"
                            type="date"
                            value={formData.date_entree_micro}
                            onChange={handleInputChange}
                          />
                        </div>
                      )}
                      
                      <div className="flex items-center space-x-2 mt-4">
                        <Checkbox
                          id="activite_temps_partiel"
                          checked={formData.activite_temps_partiel}
                          onCheckedChange={(checked) => handleCheckboxChange('activite_temps_partiel', checked)}
                        />
                        <Label htmlFor="activite_temps_partiel" className="text-sm text-[#475569] cursor-pointer">
                          Activité à temps partiel ou moins de 9 mois/an
                        </Label>
                      </div>
                    </div>
                  </div>
                )}

                {/* ========== ÉTAPE 4 : C - BIENS/LOCAUX ========== */}
                {currentStep === 4 && (
                  <div className="wizard-step space-y-5">
                    <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 mb-4">
                      <p className="text-sm text-[#2563EB] font-medium">C - Biens du nouvel établissement</p>
                    </div>
                    
                    {formData.activite_domicile ? (
                      <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-sm text-green-800">
                          Vous avez indiqué exercer votre activité à domicile (section A2). 
                          Cette section n'est pas obligatoire dans votre cas.
                        </p>
                      </div>
                    ) : (
                      <>
                        <div className="p-4 border border-slate-200 rounded-lg">
                          <p className="font-medium text-[#0F172A] mb-3">Informations sur le local professionnel</p>
                          
                          <FormField
                            label="Nature du bien"
                            name="bien_nature"
                            value={formData.bien_nature}
                            onChange={handleInputChange}
                            placeholder="Ex: Local commercial, Bureau, Atelier..."
                            help="Type de local utilisé pour votre activité"
                            required={!formData.activite_domicile}
                          />
                          
                          <div className="mt-4">
                            <FormField
                              label="Adresse du bien"
                              name="bien_adresse"
                              value={formData.bien_adresse}
                              onChange={handleInputChange}
                              placeholder="Adresse complète si différente de A1"
                            />
                          </div>
                          
                          <div className="mt-4">
                            <FormField
                              label="Situation (bâtiment, étage, escalier)"
                              name="bien_situation"
                              value={formData.bien_situation}
                              onChange={handleInputChange}
                              placeholder="Ex: Bâtiment B, 2ème étage, escalier A"
                            />
                          </div>
                          
                          <div className="mt-4">
                            <FormField
                              label="N° lot copropriété"
                              name="bien_lot_copro"
                              value={formData.bien_lot_copro}
                              onChange={handleInputChange}
                              placeholder="Si applicable"
                            />
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4 mt-4">
                            <FormField
                              label="Occupant précédent"
                              name="bien_occupant_precedent"
                              value={formData.bien_occupant_precedent}
                              onChange={handleInputChange}
                              placeholder="Nom"
                            />
                            <FormField
                              label="Date début occupation"
                              name="bien_date_occupation"
                              type="date"
                              value={formData.bien_date_occupation}
                              onChange={handleInputChange}
                            />
                          </div>
                          
                          <div className="mt-4">
                            <FormField
                              label="Superficie totale (m²)"
                              name="bien_superficie_totale"
                              type="number"
                              value={formData.bien_superficie_totale}
                              onChange={handleInputChange}
                              placeholder="Ex: 50"
                            />
                          </div>
                        </div>
                        
                        <div className="p-4 border border-slate-200 rounded-lg">
                          <p className="font-medium text-[#0F172A] mb-3">Affectation de la superficie (m²)</p>
                          
                          <div className="grid grid-cols-2 gap-4">
                            <FormField label="Professionnel" name="superficie_professionnel" type="number" value={formData.superficie_professionnel} onChange={handleInputChange} placeholder="0" />
                            <FormField label="Industriel" name="superficie_industriel" type="number" value={formData.superficie_industriel} onChange={handleInputChange} placeholder="0" />
                            <FormField label="Commercial" name="superficie_commercial" type="number" value={formData.superficie_commercial} onChange={handleInputChange} placeholder="0" />
                            <FormField label="Habitation" name="superficie_habitation" type="number" value={formData.superficie_habitation} onChange={handleInputChange} placeholder="0" />
                          </div>
                        </div>
                        
                        <div className="p-4 border border-slate-200 rounded-lg">
                          <p className="font-medium text-[#0F172A] mb-3">Détail surfaces professionnelles (m²)</p>
                          
                          <div className="grid grid-cols-2 gap-4">
                            <FormField label="Magasin" name="surface_magasin" type="number" value={formData.surface_magasin} onChange={handleInputChange} placeholder="0" />
                            <FormField label="Entrepôt" name="surface_entrepot" type="number" value={formData.surface_entrepot} onChange={handleInputChange} placeholder="0" />
                            <FormField label="Bureaux" name="surface_bureaux" type="number" value={formData.surface_bureaux} onChange={handleInputChange} placeholder="0" />
                          </div>
                          <div className="mt-4">
                            <FormField label="Autres (préciser)" name="surface_autres" value={formData.surface_autres} onChange={handleInputChange} placeholder="Ex: 10m² atelier" />
                          </div>
                        </div>
                        
                        <div className="p-4 border border-slate-200 rounded-lg">
                          <p className="font-medium text-[#0F172A] mb-3">Statut d'occupation</p>
                          
                          <div className="space-y-2">
                            {[
                              { name: "statut_proprietaire", label: "Propriétaire" },
                              { name: "statut_locataire", label: "Locataire" },
                              { name: "statut_sous_locataire", label: "Sous-locataire" },
                              { name: "statut_domiciliation_commerciale", label: "Domiciliation commerciale" },
                              { name: "statut_domiciliation_gratuite", label: "Domiciliation à titre gratuit" },
                            ].map((item) => (
                              <div key={item.name} className="flex items-center space-x-2">
                                <Checkbox
                                  id={item.name}
                                  checked={formData[item.name]}
                                  onCheckedChange={(checked) => handleCheckboxChange(item.name, checked)}
                                />
                                <Label htmlFor={item.name} className="text-sm text-[#475569] cursor-pointer">
                                  {item.label}
                                </Label>
                              </div>
                            ))}
                          </div>
                          
                          {(formData.statut_locataire || formData.statut_sous_locataire || formData.statut_domiciliation_commerciale || formData.statut_domiciliation_gratuite) && (
                            <div className="mt-4">
                              <FormField
                                label="Nom et adresse du propriétaire"
                                name="proprietaire_nom_adresse"
                                value={formData.proprietaire_nom_adresse}
                                onChange={handleInputChange}
                                placeholder="Nom et adresse complète du propriétaire"
                                help="Joindre une copie du bail ou contrat de domiciliation au SIE"
                              />
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* ========== ÉTAPE 5 : D - EXONÉRATIONS ========== */}
                {currentStep === 5 && (
                  <div className="wizard-step space-y-5">
                    <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 mb-4">
                      <p className="text-sm text-[#2563EB] font-medium">D - Exonérations demandées</p>
                    </div>
                    
                    <div className="p-4 border border-slate-200 rounded-lg">
                      <p className="font-medium text-[#0F172A] mb-3">Exonérations (cochez si applicable)</p>
                      
                      <div className="space-y-3">
                        <div className="flex items-start space-x-2">
                          <Checkbox
                            id="exoneration_entreprise_nouvelle"
                            checked={formData.exoneration_entreprise_nouvelle}
                            onCheckedChange={(checked) => handleCheckboxChange('exoneration_entreprise_nouvelle', checked)}
                          />
                          <div>
                            <Label htmlFor="exoneration_entreprise_nouvelle" className="text-sm text-[#475569] cursor-pointer">
                              Entreprise nouvelle (art. 1464 B du CGI)
                            </Label>
                            <p className="text-xs text-[#94A3B8]">Exonération première année d'activité</p>
                          </div>
                        </div>
                        
                        <div className="flex items-start space-x-2">
                          <Checkbox
                            id="exoneration_zfu"
                            checked={formData.exoneration_zfu}
                            onCheckedChange={(checked) => handleCheckboxChange('exoneration_zfu', checked)}
                          />
                          <div>
                            <Label htmlFor="exoneration_zfu" className="text-sm text-[#475569] cursor-pointer">
                              Zone France Ruralités Revitalisation
                            </Label>
                            <p className="text-xs text-[#94A3B8]">Si votre établissement est situé en zone éligible</p>
                          </div>
                        </div>
                        
                        <div className="flex items-start space-x-2">
                          <Checkbox
                            id="exoneration_autre"
                            checked={formData.exoneration_autre}
                            onCheckedChange={(checked) => handleCheckboxChange('exoneration_autre', checked)}
                          />
                          <div>
                            <Label htmlFor="exoneration_autre" className="text-sm text-[#475569] cursor-pointer">
                              Autre exonération
                            </Label>
                          </div>
                        </div>
                        
                        {formData.exoneration_autre && (
                          <div className="pl-6">
                            <FormField
                              label="Précisez l'exonération demandée"
                              name="exoneration_precision"
                              value={formData.exoneration_precision}
                              onChange={handleInputChange}
                              placeholder="Ex: Art. 1465 A - Zone de revitalisation rurale"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Année déclaration */}
                    <div className="p-4 border border-slate-200 rounded-lg">
                      <FormField
                        label="Année de la déclaration"
                        name="annee_declaration"
                        value={formData.annee_declaration}
                        onChange={handleInputChange}
                        help="Année de création ou changement d'exploitant"
                        required
                      />
                    </div>
                    
                    {/* Récapitulatif */}
                    <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                      <h4 className="font-medium text-[#0F172A] mb-3">Récapitulatif de votre déclaration</h4>
                      <div className="space-y-2 text-sm text-[#475569]">
                        <p><span className="font-medium">Déclarant :</span> {formData.denomination}</p>
                        <p><span className="font-medium">SIRET :</span> {formData.siret}</p>
                        <p><span className="font-medium">Activité :</span> {formData.activite_principale}</p>
                        <p><span className="font-medium">Adresse :</span> {formData.adresse_ligne1}, {formData.code_postal} {formData.ville}</p>
                        <p><span className="font-medium">Date création :</span> {formData.date_creation_entreprise}</p>
                        <p><span className="font-medium">Micro-entrepreneur :</span> {formData.micro_entrepreneur ? "Oui" : "Non"}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Navigation Buttons */}
                <div className="flex flex-col gap-4 mt-8 pt-6 border-t border-slate-100">
                  {/* Main CTA for last step */}
                  {currentStep === STEPS.length && (
                    <div className="flex flex-col items-center gap-3">
                      {/* Preview Button */}
                      <Button
                        variant="outline"
                        onClick={handlePreviewPDF}
                        disabled={isSubmitting || !formData.denomination || !formData.siret}
                        className="h-12 px-6 border-[#2563EB] text-[#2563EB] hover:bg-blue-50 w-full sm:w-auto"
                        data-testid="btn-apercu-pdf"
                      >
                        <Eye className="mr-2 h-4 w-4" />
                        Aperçu du PDF
                      </Button>
                      {(!formData.denomination || !formData.siret) && (
                        <p className="text-xs text-[#94A3B8] text-center">
                          Veuillez terminer le formulaire pour générer un aperçu.
                        </p>
                      )}
                      
                      {/* Main Submit Button */}
                      <Button
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className="h-14 px-8 bg-[#2563EB] hover:bg-[#1d4ed8] text-white btn-primary w-full sm:w-auto text-lg"
                        data-testid="btn-generer-pdf"
                      >
                        {isSubmitting ? (
                          <>
                            <div className="spinner mr-2" />
                            Génération...
                          </>
                        ) : (
                          <>
                            Générer mon PDF CFE
                            <FileText className="ml-2 h-5 w-5" />
                          </>
                        )}
                      </Button>
                      
                      {/* Price */}
                      <p className="text-sm text-[#64748B] text-center">
                        Prix : <span className="font-semibold text-[#0F172A]">7,99 €</span>
                      </p>
                    </div>
                  )}
                  
                  {/* Navigation for other steps */}
                  {currentStep < STEPS.length && (
                    <div className="flex flex-col-reverse sm:flex-row sm:justify-between gap-3 sm:gap-0">
                      {currentStep > 1 ? (
                        <Button
                          variant="outline"
                          onClick={prevStep}
                          className="h-12 px-6 border-slate-300 w-full sm:w-auto"
                          data-testid="btn-precedent"
                        >
                          <ChevronLeft className="mr-2 h-4 w-4" />
                          Précédent
                        </Button>
                      ) : (
                        <div className="hidden sm:block" />
                      )}
                      
                      <Button
                        onClick={nextStep}
                        className="h-12 px-6 bg-[#2563EB] hover:bg-[#1d4ed8] text-white btn-primary w-full sm:w-auto"
                        data-testid="btn-suivant"
                      >
                        Suivant
                        <ChevronRight className="ml-2 h-4 w-4" />
                      </Button>
                    </div>
                  )}
                  
                  {/* Back button for last step */}
                  {currentStep === STEPS.length && (
                    <div className="flex justify-center">
                      <Button
                        variant="ghost"
                        onClick={prevStep}
                        className="text-[#475569]"
                        data-testid="btn-precedent-final"
                      >
                        <ChevronLeft className="mr-2 h-4 w-4" />
                        Précédent
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ) : (
            /* Preview Section */
            <Card className="bg-white border-slate-200 shadow-xl" data-testid="preview-card">
              <CardHeader>
                <CardTitle className="text-2xl text-[#0F172A]">Aperçu de votre déclaration</CardTitle>
                <p className="text-[#475569]">Votre document 1447-C-SD a été généré avec succès</p>
              </CardHeader>
              <CardContent>
                {/* PDF Preview Mock */}
                <div className="pdf-document relative mb-6" data-testid="pdf-preview">
                  <div className="pdf-preview-blur">
                    <div className="text-center mb-4">
                      <h3 className="text-sm font-bold text-[#0F172A]">DÉCLARATION INITIALE DE CFE</h3>
                      <p className="text-xs text-[#475569]">Formulaire 1447-C-SD</p>
                    </div>
                    <div className="space-y-3 text-xs">
                      <div className="border border-slate-200 p-2 rounded bg-slate-50">
                        <p className="font-semibold text-[#0F172A]">A1 - IDENTIFICATION</p>
                        <p className="text-[#475569]">Dénomination : {formData.denomination}</p>
                        <p className="text-[#475569]">SIRET : {formData.siret} | NACE : {formData.code_nace}</p>
                        <p className="text-[#475569]">Adresse : {formData.adresse_ligne1}{formData.adresse_ligne2 ? `, ${formData.adresse_ligne2}` : ''}</p>
                        <p className="text-[#475569]">{formData.code_postal} {formData.ville}</p>
                      </div>
                      <div className="border border-slate-200 p-2 rounded bg-slate-50">
                        <p className="font-semibold text-[#0F172A]">A2/A3 - ACTIVITÉ & ORIGINE</p>
                        <p className="text-[#475569]">Activité : {formData.activite_principale}</p>
                        <p className="text-[#475569]">À domicile : {formData.activite_domicile ? "Oui" : "Non"}</p>
                      </div>
                      <div className="border border-slate-200 p-2 rounded bg-slate-50">
                        <p className="font-semibold text-[#0F172A]">B - ENTREPRISE</p>
                        <p className="text-[#475569]">Date création : {formData.date_creation_entreprise}</p>
                        <p className="text-[#475569]">Micro-entrepreneur : {formData.micro_entrepreneur ? "Oui" : "Non"}</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Lock Overlay */}
                  <div className="lock-overlay" data-testid="lock-overlay">
                    <div className="bg-white rounded-full p-4 shadow-lg mb-4">
                      <Lock className="h-8 w-8 text-[#94A3B8]" />
                    </div>
                    <p className="text-[#475569] text-center font-medium mb-2">Document complet disponible après paiement</p>
                    <p className="text-sm text-[#94A3B8] text-center">Inclut toutes les sections et la zone signature</p>
                  </div>
                </div>

                {/* View Preview Button */}
                <div className="flex justify-center mb-6">
                  <a
                    href={`${API}/declarations/${declarationId}/preview`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#2563EB] hover:underline text-sm flex items-center gap-1"
                    data-testid="link-preview-pdf"
                  >
                    <FileText className="h-4 w-4" />
                    Voir l'aperçu PDF dans un nouvel onglet
                  </a>
                </div>

                {/* Payment CTA */}
                <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-[#0F172A] mb-1">7,99 €</p>
                    <p className="text-sm text-[#475569] mb-4">Paiement unique • Aucun abonnement</p>
                    
                    <Button
                      onClick={handlePayment}
                      size="lg"
                      className="w-full h-14 bg-[#2563EB] hover:bg-[#1d4ed8] text-white text-lg btn-primary"
                      data-testid="btn-payer"
                    >
                      <Shield className="mr-2 h-5 w-5" />
                      Télécharger le PDF complet
                    </Button>
                    
                    <div className="flex items-center justify-center gap-4 mt-4 text-xs text-[#94A3B8]">
                      <span className="flex items-center gap-1">
                        <Shield className="h-3 w-3" />
                        Paiement sécurisé Stripe
                      </span>
                      <span className="flex items-center gap-1">
                        <Download className="h-3 w-3" />
                        Téléchargement immédiat
                      </span>
                    </div>
                  </div>
                </div>

                {/* Back button */}
                <Button
                  variant="ghost"
                  onClick={() => setShowPreview(false)}
                  className="w-full mt-4 text-[#475569]"
                  data-testid="btn-modifier"
                >
                  <ChevronLeft className="mr-2 h-4 w-4" />
                  Modifier ma déclaration
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0F172A] mb-4">Questions fréquentes</h2>
            <p className="text-[#475569]">Tout ce que vous devez savoir sur la CFE et notre service</p>
          </div>
          
          <Accordion type="single" collapsible className="space-y-4" data-testid="faq-accordion">
            {FAQ_ITEMS.map((item, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="bg-slate-50 rounded-lg border border-slate-200 px-6"
                data-testid={`faq-item-${index}`}
              >
                <AccordionTrigger className="text-left font-medium text-[#0F172A] hover:no-underline py-4">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="text-[#475569] pb-4">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* SEO Reassurance Line */}
      <div className="bg-slate-100 border-t border-slate-200 py-4 px-4 sm:px-6 lg:px-8 text-center">
        <p className="text-sm text-[#64748B]">
          Formulaire conforme au modèle officiel 1447-C-SD – à vérifier avant envoi.
        </p>
      </div>

      {/* Section complémentaire - Autres documents */}
      <div className="bg-slate-50 border-t border-slate-200 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto text-center">
          <h3 className="text-base font-medium text-[#475569] mb-2">
            Autres documents administratifs à générer ?
          </h3>
          <p className="text-sm text-[#94A3B8] mb-3">
            Pour créer facilement vos documents PDF administratifs (attestations, formulaires simples, documents officiels), découvrez notre outil en ligne.
          </p>
          <a 
            href="https://pdf-facile.fr" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center text-sm text-[#2563EB] hover:text-[#1d4ed8] transition-colors"
          >
            👉 Découvrir PDF Facile
          </a>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#0F172A] text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <CFELogo size={28} />
                <span className="font-bold text-lg">CFE Auto-Entrepreneur</span>
              </div>
              <p className="text-slate-400 text-sm">
                Service de génération de déclaration CFE simplifié pour les auto-entrepreneurs français.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Liens utiles</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#formulaire" className="footer-link hover:text-[#2563EB]">Remplir ma déclaration</a></li>
                <li><a href="#faq" className="footer-link hover:text-[#2563EB]">FAQ</a></li>
                <li><a href="/comment-remplir-cfe-auto-entrepreneur" className="footer-link hover:text-[#2563EB]">Guide CFE auto-entrepreneur</a></li>
                <li><a href="/mentions-legales" className="footer-link hover:text-[#2563EB]" data-testid="link-mentions-legales">Mentions légales</a></li>
                <li><a href="/cgv" className="footer-link hover:text-[#2563EB]" data-testid="link-cgv">CGV</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Informations importantes</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li>Ce service n'est pas affilié à l'administration fiscale française.</li>
                <li>Ce service ne constitue pas un conseil fiscal.</li>
                <li>Le document généré doit être vérifié par l'utilisateur avant envoi.</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <p className="text-slate-400 text-sm mb-2">Une question ? Contactez-nous :</p>
              <a 
                href="mailto:contact@cfe-autoentrepreneur.fr" 
                className="text-sm text-slate-400 hover:text-[#2563EB] transition-colors"
              >
                contact@cfe-autoentrepreneur.fr
              </a>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-8 pt-8 text-center text-sm text-slate-400">
            <p>© {currentYear} CFE Auto-Entrepreneur. Tous droits réservés.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
